import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, signUp, resendEmailConfirmation } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../hooks/useNotification';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import { Mail, LogIn, UserPlus, KeyRound } from 'lucide-react';
import './AuthPage.css';

/**
 * AuthPage component handles user authentication including login, signup, and password reset
 */
function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loginError, setLoginError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const navigate = useNavigate();
  const { forgotPassword, isAuthenticated, loading: authLoading } = useAuth();
  const { showError, showSuccess } = useNotification();
  const [searchParams] = useSearchParams();

  // Redirect authenticated users away from auth page
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/home');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Handle email confirmation success
  useEffect(() => {
    if (searchParams.get('type') === 'signup') {
      showSuccess('🎉 Welcome! Your email has been confirmed and you are now signed in.');
    }
  }, [searchParams, showSuccess]);

  // Set initial mode based on URL parameters
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signup') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, [searchParams]);

  /**
   * Handles input changes and clears errors when user starts typing
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear login error when user starts typing
    if (loginError) {
      setLoginError('');
    }
  };

  /**
   * Checks if error message indicates email confirmation is needed
   */
  const isEmailConfirmationError = (error) => {
    if (!error?.message) return false;
    const message = error.message.toLowerCase();
    return (
      message.includes('email not confirmed') ||
      message.includes('email not verified') ||
      message.includes('confirm your email') ||
      message.includes('verify your email')
    );
  };

  /**
   * Handles resending email confirmation
   */
  const handleResendConfirmation = async (email) => {
    try {
      const { error: resendError } = await resendEmailConfirmation(email);
      if (
        resendError &&
        !(resendError.message || '').toLowerCase().includes('already registered')
      ) {
        showError(
          `❌ Oops! We couldn't resend the confirmation email. ${resendError.message}`
        );
      }
      showSuccess('📧 Confirmation email sent! Check your inbox and spam folder.');
    } catch (error) {
      console.error('Resend confirmation error:', error);
      showError(
        '❌ Unable to resend confirmation email. Please try again in a moment.'
      );
    }
  };

  /**
   * Shows email confirmation error with resend option
   */
  const showEmailConfirmationError = (email) => {
    showError(
      'Almost there! Please check your email and click the confirmation link to verify your account before signing in.',
      {
        text: 'Resend Confirmation',
        onClick: () => handleResendConfirmation(email),
        closeOnClick: true,
        style: { backgroundColor: '#ffd136', color: 'black' },
      }
    );
  };

  /**
   * Handles login logic
   */
  const handleLogin = async () => {
    const { data, error } = await signIn(formData.email, formData.password);

    if (error) {
      if (isEmailConfirmationError(error)) {
        showEmailConfirmationError(formData.email);
      } else {
        setLoginError('Incorrect credentials, please try again');
      }
      return;
    }

    console.log('Login successful:', data);
    setLoginError('');
    navigate('/home');
  };

  /**
   * Handles signup logic
   */
  const handleSignup = async () => {
    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      showError(
        '🔒 Passwords do not match. Please make sure both password fields are identical.'
      );
      return;
    }

    const { data, error } = await signUp(formData.email, formData.password);

    if (error) {
      if (isEmailConfirmationError(error)) {
        showEmailConfirmationError(formData.email);
      } else {
        showError(
          `❌ Signup failed: ${error.message}. Please try again or contact support if the issue persists.`
        );
      }
      return;
    }

    console.log('Signup successful:', data);

    // If auto-confirm is enabled, navigate to home
    if (data?.session) {
      navigate('/home');
      return;
    }

    // Redirect to email confirmation page
    navigate(`/confirm-email?email=${encodeURIComponent(formData.email)}`);
  };

  /**
   * Handles form submission for both login and signup
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set appropriate loading state
    if (isLogin) {
      setIsSigningIn(true);
    } else {
      setIsSigningUp(true);
    }

    try {
      if (isLogin) {
        await handleLogin();
      } else {
        await handleSignup();
      }
    } catch (error) {
      console.error('Auth error:', error);
      showError('⚠️ Something went wrong. Please try again or refresh the page.');
    } finally {
      if (isLogin) {
        setIsSigningIn(false);
      } else {
        setIsSigningUp(false);
      }
    }
  };

  /**
   * Toggles between login and signup modes
   */
  const toggleMode = () => {
    setIsLogin(!isLogin);
    setShowForgotPassword(false);
    setFormData({ email: '', password: '', confirmPassword: '' });
    setLoginError('');
  };

  /**
   * Handles password reset request
   */
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      showError('Please enter your email address to reset your password.');
      return;
    }

    try {
      setIsSendingReset(true);
      const result = await forgotPassword(formData.email);

      if (result.success) {
        showSuccess(
          'Password reset email sent! Check your inbox and follow the instructions to reset your password.'
        );
        setShowForgotPassword(false);
        setFormData({ email: '', password: '', confirmPassword: '' });
      } else {
        showError(`Unable to send reset email: ${result.error}. Please try again.`);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      showError('Something went wrong. Please try again or refresh the page.');
    } finally {
      setIsSendingReset(false);
    }
  };

  /**
   * Toggles forgot password form visibility
   */
  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
    setFormData({ email: '', password: '', confirmPassword: '' });
  };

  return (
    <div className="auth-page">
      <Navbar />

      <div className="content">
        <div className="auth-header">
          <h1>
            {showForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p>
            {showForgotPassword
              ? "Enter your email address and we'll send you a link to reset your password"
              : isLogin
                ? 'Sign in to your account'
                : 'Join us to start creating tiniegos'}
          </p>
        </div>

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              className="submit-btn"
              isLoading={isSendingReset}
              disabled={isSendingReset}
              icon={Mail}
            >
              Send Reset Link
            </Button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
              />
            </div>

            {!isLogin && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirm your password"
                />
              </div>
            )}

            {isLogin && loginError && <div className="login-error-message">{loginError}</div>}

            <Button
              type="submit"
              variant="primary"
              className="submit-btn"
              isLoading={isSigningIn || isSigningUp}
              disabled={isSigningIn || isSigningUp}
              icon={isLogin ? LogIn : UserPlus}
            >
              {isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        )}

        <div className="auth-footer">
          {showForgotPassword ? (
            <p>
              Remember your password?
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={toggleForgotPassword}
                className="toggle-btn"
                icon={LogIn}
              >
                Sign In
              </Button>
            </p>
          ) : (
            <>
              {isLogin && (
                <p>
                  Forgot your password?
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={toggleForgotPassword}
                    className="toggle-btn"
                  >
                    Reset Password
                  </Button>
                </p>
              )}
              <p>
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleMode}
                  className="toggle-btn"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </Button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
